import { ReportFilters } from '../types/types';

interface LinkedAction {
  ID: number;
  Title: string;
  Created: string;
  Status: {
    Title: string;
    ID: number;
  };
  linkedReportAction: {
    quarter: string;
    year: number;
    group: {
      id: number;
      title: string;
    };
    factory: {
      id: number;
      title: string;
    };
    gop: {
      id: number;
      title: string;
    };
  };
}

export interface ReportAnswer {
  id: number;
  documentId: string;
  answer: string;
  quarter: string;
  year: number;
  factory: {
    id: number;
    documentId: string;
    title: string;
  };
  direction: {
    id: number;
    documentId: string;
    Title: string;
  };
  gop: {
    id: number;
    documentId: string;
    title: string;
  };
  requirement: {
    id: number;
    documentId: string;
    Title: string;
    isFocal: boolean | null;
  };
}

export interface AnswerQuestion {
  answer: 'Да' | 'Нет' | 'N/A';
  department: string;
  factory?: string;
  quarter?: string;
  year?: number;
  direction?: string;
  requirement?: string;
  isFocal?: boolean;
  answerId?: number | null;
}

export interface Requirement {
  answers: AnswerQuestion[];
  name: string;
  isFocal?: boolean;
}

export interface Block {
  groupRequirements: Requirement[];
  name: string;
  direction?: string;
}

export type Quarter = 'Q1' | 'Q2' | 'Q3' | 'Q4';

export interface ProcessedReportData {
  data: Array<{ value: number; label: string }>;
  openActions: Array<{ value: number; label: string }>;
  closeActions: Array<{ value: number; label: string }>;
}

export interface CompleteFilters {
  factories?: string[];
  quarters?: string[];
  years?: string[];
  directions?: string[];
  gops?: string[];
  isFocal?: boolean;
}

export const transformFiltersToApiFormat = (currentFilters: any, activeDirection: number | null, focusGop: any, availableFactories?: any[], gopDirections?: any[]): CompleteFilters => {
  const filters: CompleteFilters = {};
  if (currentFilters?.factoryGop?.selectedIds?.length > 0) {
    filters.factories = currentFilters.factoryGop.selectedIds.map((id: number) => String(id));
  }
  if (currentFilters?.quarter?.selected?.length > 0) {
    filters.quarters = currentFilters.quarter.selected;
  } else {
    filters.quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
  }
  if (currentFilters?.year?.selected?.length > 0) {
    filters.years = currentFilters.year.selected.map((year: string) => String(year));
  } else {
    const currentYear = new Date().getFullYear().toString();
    filters.years = [currentYear];
  }
  if (activeDirection) {
    filters.directions = [String(activeDirection)];
  } else if (gopDirections && gopDirections.length > 0) {
    filters.directions = gopDirections.map((d) => String(d.id || d.ID));
  }
  if (currentFilters?.formGop?.selectedIds?.length > 0) {
    filters.gops = currentFilters.formGop.selectedIds.map((id: number) => String(id));
  }
  if (currentFilters?.focusGop?.selected?.length > 0) {
    const selectedTitles = currentFilters.focusGop.selected;
    if (selectedTitles.length === 2) {
    } else if (selectedTitles.length === 1) {
      const selectedFocalTitle = selectedTitles[0];
      const focalItem = focusGop?.find((focal: any) => focal.Title === selectedFocalTitle);
      if (focalItem && focalItem.Value !== undefined) {
        filters.isFocal = focalItem.Value;
      }
    }
  }

  return filters;
};

export const calculateCompletionRate = (answers: AnswerQuestion[]): number => {
  if (!answers || answers.length === 0) {
    return 0;
  }
  const validAnswers = answers.filter((answer) => answer.answer !== 'N/A');
  if (validAnswers.length === 0) {
    return 0;
  }

  const { sum, count } = validAnswers.reduce(
    (acc, question) => {
      if (question.answer === 'Да') {
        acc.sum += 100;
        acc.count += 1;
      } else if (question.answer === 'Нет') {
        acc.sum += 0;
        acc.count += 1;
      }
      return acc;
    },
    { sum: 0, count: 0 }
  );

  return count > 0 ? Math.round(sum / count) : 0;
};

export const transformReportAnswersToQuestions = (reportAnswers: ReportAnswer[]): AnswerQuestion[] => {
  return reportAnswers.map((answer) => ({
    answer: answer.answer as 'Да' | 'Нет' | 'N/A',
    department: answer.factory.title,
    factory: answer.factory.title,
    quarter: answer.quarter,
    year: answer.year,
    direction: answer.direction.Title,
    requirement: answer.requirement.Title,
    isFocal: answer.requirement.isFocal || false,
    answerId: answer.id,
  }));
};

export const formatQuarter = (quarter: string, year: number): string => {
  const quarterMap: { [key: string]: string } = {
    Q1: 'I кв.',
    Q2: 'II кв.',
    Q3: 'III кв.',
    Q4: 'IV кв.',
  };
  return `${quarterMap[quarter] || quarter} ${year}`;
};

export const processReportDataByQuarters = (reportAnswers: ReportAnswer[]): ProcessedReportData => {
  if (!reportAnswers || reportAnswers.length === 0) {
    return getDefaultReportData();
  }
  const allAnswers = transformReportAnswersToQuestions(reportAnswers);
  const quarterData = allAnswers.reduce((acc, answer) => {
    const quarterKey = `${answer.quarter}-${answer.year}`;

    if (!acc[quarterKey]) {
      acc[quarterKey] = {
        quarter: answer.quarter!,
        year: answer.year!,
        label: formatQuarter(answer.quarter!, answer.year!),
        answers: [],
      };
    }

    acc[quarterKey].answers.push(answer);
    return acc;
  }, {} as Record<string, any>);
  const currentYear = reportAnswers.length > 0 ? reportAnswers[0].year : new Date().getFullYear();
  const allQuarters = ['Q1', 'Q2', 'Q3', 'Q4'].map((quarter) => {
    const quarterKey = `${quarter}-${currentYear}`;

    if (quarterData[quarterKey]) {
      return quarterData[quarterKey];
    } else {
      return {
        quarter,
        year: currentYear,
        label: formatQuarter(quarter, currentYear),
        answers: [],
      };
    }
  });

  const data = allQuarters.map((q: any) => {
    const completionRate = calculateCompletionRate(q.answers);
    return {
      value: completionRate,
      label: q.label,
    };
  });

  return { data };
};

export const processReportDataByFactories = (reportAnswers: ReportAnswer[]): ProcessedReportData => {
  if (!reportAnswers || reportAnswers.length === 0) {
    return getDefaultReportData();
  }

  const allAnswers = transformReportAnswersToQuestions(reportAnswers);

  const factoryData = allAnswers.reduce((acc, answer) => {
    const factoryName = answer.factory!;

    if (!acc[factoryName]) {
      acc[factoryName] = [];
    }

    acc[factoryName].push(answer);
    return acc;
  }, {} as Record<string, AnswerQuestion[]>);

  const factories = Object.entries(factoryData).map(([factoryName, answers]) => {
    const completionRate = calculateCompletionRate(answers);
    const completed = answers.filter((a) => a.answer === 'Да').length;
    const notCompleted = answers.filter((a) => a.answer === 'Нет').length;

    return {
      name: factoryName.length > 15 ? factoryName.substring(0, 15) + '...' : factoryName,
      fullName: factoryName,
      value: completionRate,
      completed,
      notCompleted,
    };
  });

  factories.sort((a, b) => b.value - a.value);

  return {
    data: factories.map((f) => ({ value: f.value, label: f.name })),
    openActions: factories.map((f) => ({ value: f.notCompleted, label: f.name })),
    closeActions: factories.map((f) => ({ value: f.completed, label: f.name })),
  };
};

export const processReportDataByDirections = (reportAnswers: ReportAnswer[]): ProcessedReportData => {
  if (!reportAnswers || reportAnswers.length === 0) {
    return getDefaultReportData();
  }

  const allAnswers = transformReportAnswersToQuestions(reportAnswers);

  const directionData = allAnswers.reduce((acc, answer) => {
    const directionName = answer.direction!;

    if (!acc[directionName]) {
      acc[directionName] = [];
    }

    acc[directionName].push(answer);
    return acc;
  }, {} as Record<string, AnswerQuestion[]>);

  const directions = Object.entries(directionData).map(([directionName, answers]) => {
    const completionRate = calculateCompletionRate(answers);
    const completed = answers.filter((a) => a.answer === 'Да').length;
    const notCompleted = answers.filter((a) => a.answer === 'Нет').length;

    return {
      name: directionName.length > 20 ? directionName.substring(0, 20) + '...' : directionName,
      fullName: directionName,
      value: completionRate,
      completed,
      notCompleted,
    };
  });

  directions.sort((a, b) => b.value - a.value);

  return {
    data: directions.map((d) => ({ value: d.value, label: d.name })),
    openActions: directions.map((d) => ({ value: d.notCompleted, label: d.name })),
    closeActions: directions.map((d) => ({ value: d.completed, label: d.name })),
  };
};

export const getDefaultReportData = (): ProcessedReportData => ({
  data: [
    { value: 0, label: 'I кв.' },
    { value: 0, label: 'II кв.' },
    { value: 0, label: 'III кв.' },
    { value: 0, label: 'IV кв.' },
  ],
  openActions: [
    { value: 0, label: 'I кв.' },
    { value: 0, label: 'II кв.' },
    { value: 0, label: 'III кв.' },
    { value: 0, label: 'IV кв.' },
  ],
  closeActions: [
    { value: 0, label: 'I кв.' },
    { value: 0, label: 'II кв.' },
    { value: 0, label: 'III кв.' },
    { value: 0, label: 'IV кв.' },
  ],
});

export const isNotStartedQuestionnaire = (data: Block[]) => {
  return data.every((block) => block.groupRequirements.every((requirement) => requirement.answers.every((answer) => answer.answerId === null)));
};

const quarterToIndex = (quarter: Quarter): number => {
  return { Q1: 0, Q2: 1, Q3: 2, Q4: 3 }[quarter];
};

export const getQuarterNumber = (year: number, quarter: Quarter): number => {
  return year * 4 + quarterToIndex(quarter);
};

export const filterByRange = (data: any[], quarter: Quarter, year: number): any[] => {
  const target = getQuarterNumber(year, quarter);

  return data.filter(({ showElement }) => {
    if (!showElement) return true;

    const start = getQuarterNumber(showElement.startYear, showElement.startQuarter);

    const finish = getQuarterNumber(showElement.finishYear, showElement.finishQuarter);

    return target >= start && target <= finish;
  });
};

export function getCurrentQuarterAndYear(data: any[]) {
  const current = new Date();

  for (const item of data) {
    const start = new Date(item.startDate);
    const end = new Date(item.finishDate);

    if (current >= start && current <= end) {
      return {
        quarter: item.quarter,
        year: item.year,
      };
    }
  }

  return null;
}

export const convertToMegabytes = (bytes: number): number => {
  const megabytes = bytes / (1024 * 1024);
  return Math.round(megabytes);
};

export const transformTitle = (el: { [key: string]: string | number }) => {
  const { title, ...rest } = el;
  return { ...rest, name: title };
};

export const getAnswersStatistics = (reportAnswers: ReportAnswer[]) => {
  if (!reportAnswers || reportAnswers.length === 0) {
    return { total: 0, completed: 0, notCompleted: 0, naCount: 0, completionRate: 0 };
  }

  const total = reportAnswers.length;
  const completed = reportAnswers.filter((a) => a.answer === 'Да').length;
  const notCompleted = reportAnswers.filter((a) => a.answer === 'Нет').length;
  const naCount = reportAnswers.filter((a) => a.answer === 'N/A').length;
  const validAnswers = total - naCount;

  const completionRate = validAnswers > 0 ? Math.round((completed * 100) / validAnswers) : 0;

  return { total, completed, notCompleted, naCount, completionRate };
};

//! опр квартала из даты
const getQuarterFromDate = (dateString: string): string => {
  const date = new Date(dateString);
  const month = date.getMonth() + 1;

  if (month >= 1 && month <= 3) return 'Q1';
  if (month >= 4 && month <= 6) return 'Q2';
  if (month >= 7 && month <= 9) return 'Q3';
  return 'Q4';
};

const isOpenAction = (status: string): boolean => {
  return ['Не начато', 'В процессе', 'Просрочено'].includes(status);
};

const isClosedAction = (status: string): boolean => {
  return ['Отменено', 'Закрыто'].includes(status);
};

const getCumulativeValues = (values: number[]): number[] => {
  let sum = 0;
  return values.map((value) => {
    sum += value;
    return sum;
  });
};

export const processActionsDataByQuarters = (
  linkedActions: LinkedAction[],
  currentYear?: number
): { openActions: Array<{ value: number; label: string }>; closeActions: Array<{ value: number; label: string }> } => {
  const year = currentYear || new Date().getFullYear();
  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];

  const openActionsCounts = [0, 0, 0, 0];
  const closeActionsCounts = [0, 0, 0, 0];

  linkedActions.forEach((action) => {
    if (!action.linkedReportAction || action.linkedReportAction.year !== year) {
      return;
    }

    const createdQuarter = getQuarterFromDate(action.Created);
    const quarterIndex = quarters.indexOf(createdQuarter);
    if (quarterIndex === -1) return;
    const status = action.Status?.Title;
    if (isOpenAction(status)) {
      openActionsCounts[quarterIndex]++;
    }
    if (isClosedAction(status)) {
      closeActionsCounts[quarterIndex]++;
    }
  });

  let lastQuarterWithActions = -1;
  for (let i = 0; i < 4; i++) {
    if (openActionsCounts[i] > 0 || closeActionsCounts[i] > 0) {
      lastQuarterWithActions = i;
    }
  }

  if (lastQuarterWithActions === -1) {
    return {
      openActions: [],
      closeActions: [],
    };
  }

  const cumulativeOpenActions = getCumulativeValues(openActionsCounts);
  const cumulativeCloseActions = getCumulativeValues(closeActionsCounts);

  const visibleQuarters = lastQuarterWithActions + 1;

  const openActions = quarters.slice(0, visibleQuarters).map((quarter, index) => ({
    value: cumulativeOpenActions[index],
    label: formatQuarter(quarter, year),
  }));

  const closeActions = quarters.slice(0, visibleQuarters).map((quarter, index) => ({
    value: cumulativeCloseActions[index],
    label: formatQuarter(quarter, year),
  }));

  return { openActions, closeActions };
};

export const processReportDataByQuartersWithActions = (reportAnswers: ReportAnswer[], linkedActions: LinkedAction[] = []): ProcessedReportData => {
  const answersData = processReportDataByQuarters(reportAnswers);
  const currentYear = reportAnswers.length > 0 ? reportAnswers[0].year : new Date().getFullYear();
  const actionsData = processActionsDataByQuarters(linkedActions, currentYear);

  return {
    data: answersData.data,
    openActions: actionsData.openActions,
    closeActions: actionsData.closeActions,
  };
};
